import { Routes } from '@angular/router';

import { OwnerDashboardComponent } from './pages/dashboard/owner-dashboard/owner-dashboard.component';
import { ProfileComponent } from './pages/profile/profile.component';
import { NotFoundComponent } from './pages/other-page/not-found/not-found.component';
import { ForbiddenComponent } from './pages/forbidden/forbidden.component';
import { AppLayoutComponent } from './shared/layout/app-layout/app-layout.component';
import { InvoicesListComponent } from './pages/invoices/invoices-list/invoices-list.component';
import { InvoiceDetailComponent } from './pages/invoices/invoice-detail/invoice-detail.component';
import { TransactionsListComponent } from './pages/transactions/transactions-list/transactions-list.component';
import { TransactionDetailComponent } from './pages/transactions/transaction-detail/transaction-detail.component';
import { WalletsListComponent } from './pages/wallets/wallets-list/wallets-list.component';
import { WalletDetailComponent } from './pages/wallets/wallet-detail/wallet-detail.component';
import { SignInComponent } from './pages/auth-pages/sign-in/sign-in.component';
import { SignUpComponent } from './pages/auth-pages/sign-up/sign-up.component';
import { VehiclesListComponent } from './pages/vehicles/vehicles-list/vehicles-list.component';
import { VehicleDetailComponent } from './pages/vehicles/vehicle-detail/vehicle-detail.component';
import { RentalsListComponent } from './pages/rentals/rentals-list/rentals-list.component';
import { RentalDetailComponent } from './pages/rentals/rental-detail/rental-detail.component';

import { authGuard } from './shared/guards/auth.guard';
import { roleGuard } from './shared/guards/role.guard';

/**
 * Owner app : every route under AppLayoutComponent requires the VEHICLE_OWNER role.
 * Public routes : /signin, /signup, /forbidden, and the catch-all 404.
 *
 * Special case: `/become-owner` is allowed for any authenticated user (un user
 * connecté sans rôle owner peut s'enregistrer comme owner). Cette route est
 * placée HORS du sous-arbre AppLayoutComponent + roleGuard mais sous authGuard.
 *
 * NOTE pages owner-specific (`become-owner`, modules adaptés /me) à créer au
 * fil des prochains commits. Ce squelette retire d'abord toutes les routes
 * admin-only.
 */
export const routes: Routes = [
  // Onboarding — authentifié mais rôle owner pas requis (juste authGuard).
  // Le composant est lazy-loaded pour éviter de bloquer le bundle.
  {
    path: 'become-owner',
    canActivate: [authGuard],
    loadComponent: () =>
      import('./pages/onboarding/become-owner/become-owner.component').then(
        (m) => m.BecomeOwnerComponent,
      ),
    title: 'Nawam Owner — Devenir propriétaire',
  },

  // Sous-arbre principal — VEHICLE_OWNER requis.
  {
    path: '',
    component: AppLayoutComponent,
    canActivate: [roleGuard('VEHICLE_OWNER')],
    children: [
      { path: '', pathMatch: 'full', component: OwnerDashboardComponent, title: 'Nawam Owner — Tableau de bord' },

      // ── Ma flotte ─────────────────────────────────────────────────────
      // NOTE: `my-vehicles/new` doit précéder `my-vehicles/:id` sinon "new" est capté comme un id.
      { path: 'my-vehicles', component: VehiclesListComponent, title: 'Nawam Owner — Ma flotte' },
      {
        path: 'my-vehicles/new',
        loadComponent: () =>
          import('./pages/vehicles/vehicle-create/vehicle-create.component').then(
            (m) => m.VehicleCreateComponent,
          ),
        title: 'Nawam Owner — Nouveau véhicule',
      },
      { path: 'my-vehicles/:id', component: VehicleDetailComponent, title: 'Nawam Owner — Fiche véhicule' },
      {
        path: 'my-vehicles/:id/edit',
        loadComponent: () =>
          import('./pages/vehicles/vehicle-edit/vehicle-edit.component').then(
            (m) => m.VehicleEditComponent,
          ),
        title: 'Nawam Owner — Modifier le véhicule',
      },

      // ── Mes contrats (mise à disposition véhicules) ───────────────────
      {
        path: 'my-contracts',
        loadComponent: () =>
          import('./pages/contracts/contracts-list/contracts-list.component').then(
            (m) => m.ContractsListComponent,
          ),
        title: 'Nawam Owner — Mes contrats',
      },

      // ── Mes locations ─────────────────────────────────────────────────
      { path: 'my-rentals', component: RentalsListComponent, title: 'Nawam Owner — Mes locations' },
      { path: 'my-rentals/:id', component: RentalDetailComponent, title: 'Nawam Owner — Fiche location' },

      // ── Mes factures ──────────────────────────────────────────────────
      { path: 'my-invoices', component: InvoicesListComponent, title: 'Nawam Owner — Mes factures' },
      { path: 'my-invoices/:id', component: InvoiceDetailComponent, title: 'Nawam Owner — Fiche facture' },

      // ── Cautions à rembourser (#46) ───────────────────────────────────
      {
        path: 'deposit-refunds',
        loadComponent: () =>
          import('./pages/deposit-refunds/deposit-refunds-list/deposit-refunds-list.component').then(
            (m) => m.DepositRefundsListComponent,
          ),
        title: 'Nawam Owner — Cautions à rembourser',
      },

      // ── Mes transactions ──────────────────────────────────────────────
      { path: 'my-transactions', component: TransactionsListComponent, title: 'Nawam Owner — Mes transactions' },
      { path: 'my-transactions/:id', component: TransactionDetailComponent, title: 'Nawam Owner — Fiche transaction' },

      // ── Mon wallet ────────────────────────────────────────────────────
      { path: 'my-wallet', component: WalletsListComponent, title: 'Nawam Owner — Mon wallet' },
      { path: 'my-wallet/:ownerType/:ownerRef', component: WalletDetailComponent, title: 'Nawam Owner — Détail wallet' },

      // ── Mon opérateur (#36) ───────────────────────────────────────────
      {
        path: 'my-operator',
        loadComponent: () =>
          import('./pages/my-operator/my-operator.component').then((m) => m.MyOperatorComponent),
        title: 'Nawam Owner — Mon opérateur',
      },

      // ── Messages / chat de location ───────────────────────────────────
      {
        path: 'my-chats',
        loadComponent: () =>
          import('./pages/chats/chat-list/chat-list.component').then((m) => m.ChatListComponent),
        title: 'Nawam Owner — Messages',
      },
      {
        path: 'my-chats/:id',
        loadComponent: () =>
          import('./pages/chats/chat-conversation/chat-conversation.component').then(
            (m) => m.ChatConversationComponent,
          ),
        title: 'Nawam Owner — Conversation',
      },

      // ── Notifications ─────────────────────────────────────────────────
      {
        path: 'notifications',
        loadComponent: () =>
          import('./pages/notifications/notifications-list/notifications-list.component').then(
            (m) => m.NotificationsListComponent,
          ),
        title: 'Nawam Owner — Notifications',
      },

      // ── Profil ────────────────────────────────────────────────────────
      { path: 'profile', component: ProfileComponent, title: 'Nawam Owner — Profil' },

      // ── Atterrissage post-paiement (dispatcher payment-service) ───────
      // Le back redirige vers /payment/result/<returnPath>?txId=…&status=…
      // Le wildcard `**` matche n'importe quel returnPath ; le composant lit
      // les query params et infère le CTA de retour depuis le segment.
      {
        path: 'payment/result/**',
        loadComponent: () =>
          import('./pages/payment/payment-result/payment-result.component').then(
            (m) => m.PaymentResultComponent,
          ),
        title: 'Nawam Owner — Résultat du paiement',
      },
    ],
  },

  // ── public ────────────────────────────────────────────────────────────────
  { path: 'signin', component: SignInComponent, title: 'Nawam Owner — Connexion' },
  { path: 'signup', component: SignUpComponent, title: 'Nawam Owner — Inscription' },
  {
    path: 'auth/set-password/:token',
    loadComponent: () =>
      import('./pages/auth-pages/set-password/set-password.component').then(
        (m) => m.SetPasswordComponent,
      ),
    title: 'Nawam Owner — Définir mon mot de passe',
  },
  {
    path: 'forgotten-password',
    loadComponent: () =>
      import('./pages/auth-pages/forgotten-password/forgotten-password.component').then(
        (m) => m.ForgottenPasswordComponent,
      ),
    title: 'Nawam Owner — Mot de passe oublié',
  },
  { path: 'forbidden', component: ForbiddenComponent, title: 'Nawam Owner — Accès refusé' },

  // ── catch-all ────────────────────────────────────────────────────────────
  { path: '**', component: NotFoundComponent, title: 'Nawam Owner — Page introuvable' },
];
